import { useState, useEffect } from "react";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { 
  Truck, Car, Plus, Pencil, Trash2, Loader2, Search, X,
  Ruler, Package, Fuel, Calendar, Building2, Layers
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const initialVehicle = {
  company_id: "",
  brand: "",
  model: "",
  vehicle_type: "medio",
  engine_type: "Diesel",
  length_m: "",
  height_m: "",
  width_m: "",
  cargo_volume_m3: "",
  max_payload_kg: "",
  seats: 3,
  transmission: "Manuale",
  year: new Date().getFullYear(),
  license_plate: "",
  description: "",
  features: []
};

// Vehicle Card Component (defined outside main component)
function VehicleCard({ vehicle, typeInfo, companyName, onEdit, onDelete }) {
  const isAuto = typeInfo.category === "auto";
  
  return (
    <Card data-testid={`vehicle-card-${vehicle.vehicle_id}`}>
      <div className="aspect-video bg-muted relative">
        {vehicle.images?.[0] ? (
          <img
            src={vehicle.images[0].startsWith("http") ? vehicle.images[0] : `${process.env.REACT_APP_BACKEND_URL}${vehicle.images[0]}`}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {isAuto ? (
              <Car className="h-12 w-12 text-muted-foreground" />
            ) : (
              <Truck className="h-12 w-12 text-muted-foreground" />
            )}
          </div>
        )}
        <Badge className={`absolute top-3 right-3 ${vehicle.is_available !== false ? "bg-green-600" : "bg-red-600"}`}>
          {vehicle.is_available !== false ? "Disponibile" : "Non disponibile"}
        </Badge>
      </div>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="font-bold text-lg" style={{ fontFamily: "Chivo, sans-serif" }}>
              {vehicle.brand} {vehicle.model}
            </h3>
            <p className="text-sm text-muted-foreground">{companyName}</p>
          </div>
          {vehicle.license_plate && (
            <p className="text-xs font-mono text-muted-foreground">{vehicle.license_plate}</p>
          )}
        </div>
        
        {/* Vehicle Type Badge */}
        <div className="mb-3">
          <Badge variant="outline" className={`${isAuto ? "border-blue-500 text-blue-600" : "border-orange-500 text-orange-600"}`}>
            {isAuto ? <Car className="h-3 w-3 mr-1" /> : <Truck className="h-3 w-3 mr-1" />}
            {typeInfo.label}
          </Badge>
        </div>
        
        <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Ruler className="h-4 w-4" />
            {vehicle.length_m}m
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Package className="h-4 w-4" />
            {vehicle.cargo_volume_m3}m³
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Fuel className="h-4 w-4" />
            {vehicle.engine_type}
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Calendar className="h-4 w-4" />
            {vehicle.year}
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-border">
          <p className="text-sm text-muted-foreground">
            {vehicle.seats} posti • {vehicle.transmission}
          </p>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(vehicle)}
              data-testid={`edit-vehicle-${vehicle.vehicle_id}`}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive"
              onClick={() => onDelete(vehicle.vehicle_id)}
              data-testid={`delete-vehicle-${vehicle.vehicle_id}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Vehicle Grid Component (defined outside main component)
function VehicleGrid({ vehicles, emptyMessage, getTypeInfo, getCompanyName, onEdit, onDelete }) {
  if (vehicles.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {vehicles.map((vehicle) => (
        <VehicleCard 
          key={vehicle.vehicle_id} 
          vehicle={vehicle} 
          typeInfo={getTypeInfo(vehicle.vehicle_type)}
          companyName={getCompanyName(vehicle.company_id)}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

export default function AdminVehicles() {
  const { token } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  
  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [formData, setFormData] = useState(initialVehicle);
  const [featureInput, setFeatureInput] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [vehiclesRes, companiesRes, typesRes] = await Promise.all([
        axios.get(`${API}/vehicles`, { headers, withCredentials: true }),
        axios.get(`${API}/companies`, { headers, withCredentials: true }),
        axios.get(`${API}/vehicle-types`)
      ]);
      
      setVehicles(vehiclesRes.data);
      setCompanies(companiesRes.data);
      setVehicleTypes(typesRes.data);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const getVehicleTypeInfo = (typeId) => {
    return vehicleTypes.find(vt => vt.id === typeId) || { label: typeId, category: "altro" };
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.company_id === companyId);
    return company?.name || "N/A";
  };

  // Filter vehicles
  const filteredVehicles = vehicles.filter((v) => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!v.brand?.toLowerCase().includes(search) && 
          !v.model?.toLowerCase().includes(search) &&
          !v.license_plate?.toLowerCase().includes(search)) {
        return false;
      }
    }
    if (companyFilter && v.company_id !== companyFilter) return false;
    if (categoryFilter !== "all") {
      const typeInfo = getVehicleTypeInfo(v.vehicle_type);
      if (typeInfo.category !== categoryFilter) return false;
    }
    return true;
  });

  const furgoniVehicles = filteredVehicles.filter(v => getVehicleTypeInfo(v.vehicle_type).category === "furgone");
  const autoVehicles = filteredVehicles.filter(v => getVehicleTypeInfo(v.vehicle_type).category === "auto");
  const altriVehicles = filteredVehicles.filter(v => {
    const cat = getVehicleTypeInfo(v.vehicle_type).category;
    return cat !== "furgone" && cat !== "auto";
  });

  const openAddDialog = () => {
    setEditingVehicle(null);
    setFormData(initialVehicle);
    setShowDialog(true);
  };

  const openEditDialog = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      company_id: vehicle.company_id || "",
      brand: vehicle.brand || "",
      model: vehicle.model || "",
      vehicle_type: vehicle.vehicle_type || "medio",
      engine_type: vehicle.engine_type || "Diesel",
      length_m: vehicle.length_m || "",
      height_m: vehicle.height_m || "",
      width_m: vehicle.width_m || "",
      cargo_volume_m3: vehicle.cargo_volume_m3 || "",
      max_payload_kg: vehicle.max_payload_kg || "",
      seats: vehicle.seats || 3,
      transmission: vehicle.transmission || "Manuale",
      year: vehicle.year || new Date().getFullYear(),
      license_plate: vehicle.license_plate || "",
      description: vehicle.description || "",
      features: vehicle.features || []
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_id) {
      toast.error("Seleziona un&#39;azienda");
      return;
    }

    setSubmitting(true);

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const data = {
        ...formData,
        length_m: parseFloat(formData.length_m),
        height_m: parseFloat(formData.height_m),
        width_m: parseFloat(formData.width_m),
        cargo_volume_m3: parseFloat(formData.cargo_volume_m3),
        max_payload_kg: parseFloat(formData.max_payload_kg),
        year: parseInt(formData.year),
        seats: parseInt(formData.seats),
        license_plate: formData.license_plate || null
      };

      if (editingVehicle) {
        await axios.put(`${API}/vehicles/${editingVehicle.vehicle_id}`, data, { headers, withCredentials: true });
        toast.success("Veicolo aggiornato");
      } else {
        await axios.post(`${API}/vehicles`, data, { headers, withCredentials: true });
        toast.success("Veicolo aggiunto");
      }

      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteVehicle = async (vehicleId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo veicolo?")) return;

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/vehicles/${vehicleId}`, { headers, withCredentials: true });
      toast.success("Veicolo eliminato");
      fetchData();
    } catch (error) {
      toast.error("Errore durante l&#39;eliminazione");
    }
  };

  const addFeature = () => {
    if (featureInput.trim() && !formData.features.includes(featureInput.trim())) {
      setFormData({ ...formData, features: [...formData.features, featureInput.trim()] });
      setFeatureInput("");
    }
  };

  const removeFeature = (feature) => {
    setFormData({ ...formData, features: formData.features.filter(f => f !== feature) });
  };

  return (
    <DashboardLayout title="Gestione Veicoli">
      <div className="space-y-6" data-testid="admin-vehicles">
        {/* Header */}
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="flex flex-1 gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca per marca, modello o targa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-vehicles"
              />
            </div>
            <Select value={companyFilter || "all"} onValueChange={(v) => setCompanyFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-48" data-testid="company-filter">
                <SelectValue placeholder="Tutte le aziende" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le aziende</SelectItem>
                {companies.map((c) => (
                  <SelectItem key={c.company_id} value={c.company_id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={openAddDialog} className="btn-primary py-2" data-testid="add-vehicle-btn">
            <Plus className="h-4 w-4 mr-2" />
            Aggiungi Veicolo
          </Button>
        </div>

        {/* Stats */}
        <div className="flex gap-4 text-sm text-muted-foreground flex-wrap">
          <span>{filteredVehicles.length} veicoli totali</span>
          <span>•</span>
          <span className="flex items-center gap-1">
            <Truck className="h-4 w-4" />
            {furgoniVehicles.length} furgoni
          </span>
          <span>•</span>
          <span className="flex items-center gap-1">
            <Car className="h-4 w-4" />
            {autoVehicles.length} auto
          </span>
          <span>•</span>
          <span>{companies.length} aziende</span>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : companies.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">Nessuna azienda registrata</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Devi prima creare un&apos;azienda per poter aggiungere veicoli
              </p>
            </CardContent>
          </Card>
        ) : filteredVehicles.length === 0 && !searchTerm && !companyFilter ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Truck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">Nessun veicolo trovato</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Aggiungi il primo veicolo
              </p>
              <Button onClick={openAddDialog}>Aggiungi Veicolo</Button>
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue="all" className="w-full" onValueChange={setCategoryFilter}>
            <TabsList className="mb-6">
              <TabsTrigger value="all" className="flex items-center gap-2">
                <Layers className="h-4 w-4" />
                Tutti ({filteredVehicles.length})
              </TabsTrigger>
              <TabsTrigger value="furgone" className="flex items-center gap-2">
                <Truck className="h-4 w-4" />
                Furgoni ({furgoniVehicles.length})
              </TabsTrigger>
              <TabsTrigger value="auto" className="flex items-center gap-2">
                <Car className="h-4 w-4" />
                Auto ({autoVehicles.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="all">
              {filteredVehicles.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  Nessun veicolo corrisponde ai filtri selezionati
                </div>
              ) : (
                <>
                  {furgoniVehicles.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <Truck className="h-5 w-5 text-orange-500" />
                        Furgoni ({furgoniVehicles.length})
                      </h3>
                      <VehicleGrid 
                        vehicles={furgoniVehicles} 
                        emptyMessage="" 
                        getTypeInfo={getVehicleTypeInfo}
                        getCompanyName={getCompanyName}
                        onEdit={openEditDialog}
                        onDelete={deleteVehicle}
                      />
                    </div>
                  )}
                  {autoVehicles.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <Car className="h-5 w-5 text-blue-500" />
                        Auto ({autoVehicles.length})
                      </h3>
                      <VehicleGrid 
                        vehicles={autoVehicles} 
                        emptyMessage="" 
                        getTypeInfo={getVehicleTypeInfo}
                        getCompanyName={getCompanyName}
                        onEdit={openEditDialog}
                        onDelete={deleteVehicle}
                      />
                    </div>
                  )}
                  {altriVehicles.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <Layers className="h-5 w-5 text-gray-500" />
                        Altri ({altriVehicles.length})
                      </h3>
                      <VehicleGrid 
                        vehicles={altriVehicles} 
                        emptyMessage="" 
                        getTypeInfo={getVehicleTypeInfo}
                        getCompanyName={getCompanyName}
                        onEdit={openEditDialog}
                        onDelete={deleteVehicle}
                      />
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent value="furgone">
              <VehicleGrid 
                vehicles={furgoniVehicles} 
                emptyMessage="Nessun furgone trovato" 
                getTypeInfo={getVehicleTypeInfo}
                getCompanyName={getCompanyName}
                onEdit={openEditDialog}
                onDelete={deleteVehicle}
              />
            </TabsContent>

            <TabsContent value="auto">
              <VehicleGrid 
                vehicles={autoVehicles} 
                emptyMessage="Nessuna auto trovata" 
                getTypeInfo={getVehicleTypeInfo}
                getCompanyName={getCompanyName}
                onEdit={openEditDialog}
                onDelete={deleteVehicle}
              />
            </TabsContent>
          </Tabs>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Chivo, sans-serif" }}>
                {editingVehicle ? "Modifica Veicolo" : "Nuovo Veicolo"}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Company Selection */}
              <div>
                <Label className="input-label">Azienda *</Label>
                <Select
                  value={formData.company_id || "select"}
                  onValueChange={(value) => setFormData({ ...formData, company_id: value === "select" ? "" : value })}
                >
                  <SelectTrigger data-testid="vehicle-company">
                    <SelectValue placeholder="Seleziona azienda" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="select" disabled>Seleziona azienda</SelectItem>
                    {companies.map((c) => (
                      <SelectItem key={c.company_id} value={c.company_id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Vehicle Type */}
              <div>
                <Label className="input-label">Tipologia Veicolo *</Label>
                <Select
                  value={formData.vehicle_type}
                  onValueChange={(value) => setFormData({ ...formData, vehicle_type: value })}
                >
                  <SelectTrigger data-testid="vehicle-type">
                    <SelectValue placeholder="Seleziona tipologia" />
                  </SelectTrigger>
                  <SelectContent>
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">Furgoni</div>
                    {vehicleTypes.filter(vt => vt.category === "furgone").map((vt) => (
                      <SelectItem key={vt.id} value={vt.id}>
                        <span className="flex items-center gap-2">
                          <Truck className="h-3 w-3" />
                          {vt.label}
                        </span>
                      </SelectItem>
                    ))}
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1 pt-2">Auto</div>
                    {vehicleTypes.filter(vt => vt.category === "auto").map((vt) => (
                      <SelectItem key={vt.id} value={vt.id}>
                        <span className="flex items-center gap-2">
                          <Car className="h-3 w-3" />
                          {vt.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  La tipologia determina il prezzo in base al tariffario dell&apos;azienda
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="input-label">Marca *</Label>
                  <Input
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    placeholder="es. Fiat, BMW, Mercedes"
                    required
                    data-testid="vehicle-brand"
                  />
                </div>
                <div>
                  <Label className="input-label">Modello *</Label>
                  <Input
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    placeholder="es. Ducato, Serie 3, Classe A"
                    required
                    data-testid="vehicle-model"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="input-label">Motorizzazione *</Label>
                  <Select
                    value={formData.engine_type}
                    onValueChange={(value) => setFormData({ ...formData, engine_type: value })}
                  >
                    <SelectTrigger data-testid="vehicle-engine">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Diesel">Diesel</SelectItem>
                      <SelectItem value="Benzina">Benzina</SelectItem>
                      <SelectItem value="Elettrico">Elettrico</SelectItem>
                      <SelectItem value="Ibrido">Ibrido</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="input-label">Cambio *</Label>
                  <Select
                    value={formData.transmission}
                    onValueChange={(value) => setFormData({ ...formData, transmission: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Manuale">Manuale</SelectItem>
                      <SelectItem value="Automatico">Automatico</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="input-label">Anno *</Label>
                  <Input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                    min="2000"
                    max={new Date().getFullYear() + 1}
                    required
                    data-testid="vehicle-year"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="input-label">Lunghezza (m) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.length_m}
                    onChange={(e) => setFormData({ ...formData, length_m: e.target.value })}
                    required
                    data-testid="vehicle-length"
                  />
                </div>
                <div>
                  <Label className="input-label">Altezza (m) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.height_m}
                    onChange={(e) => setFormData({ ...formData, height_m: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label className="input-label">Larghezza (m) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.width_m}
                    onChange={(e) => setFormData({ ...formData, width_m: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="input-label">Volume Carico (m³) *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.cargo_volume_m3}
                    onChange={(e) => setFormData({ ...formData, cargo_volume_m3: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label className="input-label">Portata Max (kg) *</Label>
                  <Input
                    type="number"
                    value={formData.max_payload_kg}
                    onChange={(e) => setFormData({ ...formData, max_payload_kg: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label className="input-label">Posti *</Label>
                  <Input
                    type="number"
                    value={formData.seats}
                    onChange={(e) => setFormData({ ...formData, seats: e.target.value })}
                    min="1"
                    max="9"
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="input-label">Targa (Opzionale)</Label>
                <Input
                  value={formData.license_plate}
                  onChange={(e) => setFormData({ ...formData, license_plate: e.target.value.toUpperCase() })}
                  placeholder="AA000BB"
                  data-testid="vehicle-plate"
                />
              </div>

              <div>
                <Label className="input-label">Descrizione</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrizione del veicolo..."
                  rows={2}
                />
              </div>

              <div>
                <Label className="input-label">Dotazioni</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={featureInput}
                    onChange={(e) => setFeatureInput(e.target.value)}
                    placeholder="es. Aria condizionata, Navigatore, Sensori parcheggio"
                    onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())}
                  />
                  <Button type="button" variant="outline" onClick={addFeature}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.features.map((feature, index) => (
                    <Badge key={index} variant="secondary" className="py-1 px-2">
                      {feature}
                      <button
                        type="button"
                        onClick={() => removeFeature(feature)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Annulla
                </Button>
                <Button type="submit" disabled={submitting} className="btn-primary">
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Salvataggio...
                    </>
                  ) : (
                    editingVehicle ? "Salva Modifiche" : "Aggiungi Veicolo"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
